import { useEffect, useRef } from 'react';
import { LocationData } from './LocationTypes';
import classNames from 'classnames';
import SVG from 'helpers/SVG/SVG';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';

import { useDictionary } from 'lib/hooks/use-dictionary';

// LocationList component with scrollbar
const LocationList: React.FC<{
  errorMessage: string | null;
  locations: LocationData[];
  scrollToLocationIndex: number | null;
  setLocationItemIndex: (index: number | null) => void;
  setScrollToLocationIndex: (index: number | null) => void;
}> = ({
  errorMessage,
  locations,
  scrollToLocationIndex,
  setLocationItemIndex,
  setScrollToLocationIndex,
}) => {
  const locationRefs = useRef<(HTMLDivElement | null)[]>([]);
  const locationListRef = useRef<HTMLDivElement>(null);
  const innerLocationListRef = useRef<HTMLDivElement>(null);

  const dict = useDictionary();

  const locationName =
    dict?.['Foundation.Locations.DefaultLocationName'] || 'Financial Institution';

  const handleTopicsMaskVisibility = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (locationListRef.current) {
      const locationListElement = e.currentTarget;
      if (locationListElement.children.length > 0) {
        const topicsListElementYAxis = locationListElement.getBoundingClientRect();
        const firstTopicElementYAxis =
          locationListElement.firstElementChild?.getBoundingClientRect().y;

        const lastTopicElementYAxis =
          locationListElement.lastElementChild?.getBoundingClientRect().y;

        const beforeElementVisibileClass = 'before:!inline-block';
        const afterElementVisibileClass = 'after:!inline-block';

        // Toggle topics top mask
        if (firstTopicElementYAxis && firstTopicElementYAxis < topicsListElementYAxis.top) {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          !locationListRef.current.classList.contains(beforeElementVisibileClass) &&
            locationListRef.current.classList.add(beforeElementVisibileClass);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          locationListRef.current.classList.contains(beforeElementVisibileClass) &&
            locationListRef.current.classList.remove(beforeElementVisibileClass);
        }

        // Toggle topics bottom mask
        if (lastTopicElementYAxis && lastTopicElementYAxis > topicsListElementYAxis.bottom - 16) {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          !locationListRef.current.classList.contains(afterElementVisibileClass) &&
            locationListRef.current.classList.add(afterElementVisibileClass);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          locationListRef.current.classList.contains(afterElementVisibileClass) &&
            locationListRef.current.classList.remove(afterElementVisibileClass);
        }
      }
    }
  };

  useEffect(() => {
    if (innerLocationListRef.current) {
      // Manually trigger handleTopicsMaskVisibility on load
      handleTopicsMaskVisibility({ currentTarget: innerLocationListRef.current } as React.UIEvent<
        HTMLDivElement,
        UIEvent
      >);
    }
  }, [locations]);

  useEffect(() => {
    if (scrollToLocationIndex !== null && locationRefs.current[scrollToLocationIndex]) {
      locationRefs.current[scrollToLocationIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [scrollToLocationIndex]);

  return (
    <>
      {locations && Array.isArray(locations) && locations.length > 0 && (
        <div
          ref={locationListRef}
          className={classNames(
            'overflow-auto md:overflow-hidden relative',
            'before:hidden before:pointer-events-none before:z-10 before:absolute before:top-0 before:left-0 before:w-full before:h-24 before:bg-light-background-gradient-gray',
            'after:hidden after:pointer-events-none after:z-10 after:absolute after:bottom-0 after:left-0 after:w-full after:h-24 after:bg-light-background-gradient-gray'
          )}
        >
          <div
            ref={innerLocationListRef}
            onScroll={(e) => {
              handleTopicsMaskVisibility(e);
            }}
            className={classNames(
              `${locations.length > 0 ? 'h-[470px] mt-4' : 'md:mt-0 md:h-full'}`,
              'h-[470px] mt-4 md:mt-0 md:h-full',
              'location-list relative overflow-x-hidden overflow-y-auto scrollbar-thin scrollbar-thumb-rounded-full scrollbar-thumb-gray-400 scrollbar-track-gray-100'
            )}
          >
            {locations.map((location, index) => (
              <>
                <div
                  onClick={() => {
                    setLocationItemIndex(index + 1);
                    setScrollToLocationIndex(index + 1);
                  }}
                  ref={(el) => {
                    locationRefs.current[index + 1] = el;
                  }}
                  key={index + 1}
                  className={classNames(
                    scrollToLocationIndex == index + 1 &&
                      'shadow-small bg-light-background-neutral-variant',
                    'location-item cursor-pointer [&:not(:last-child)]:border-b border-light-stroke-primary flex justify-between py-2 px-2 md:py-4 md:px-4 min-h-[83px] md:min-h-[105px] lg:min-h-[111px]',
                    'hover:shadow-small hover:bg-light-background-neutral-variant',
                    'focus:shadow-small focus:bg-light-background-neutral-variant focus:outline-none'
                  )}
                  tabIndex={0}
                >
                  <div className="location-address flex">
                    {/* Location Icon and Number */}
                    <div className="location-icon relative flex justify-center">
                      <SVG
                        svg="icon-pin-2"
                        className="w-10 h-10 px-1 p-0 box-content fill-current [&_path]:fill-light-fill-secondary"
                      />
                      <span className="absolute top-1 c1-medium text-light-fill-inverse">
                        {index + 1}
                      </span>
                    </div>
                    {/* Location Details */}
                    <div className="relative">
                      <p className="block mb-1 p2-medium">
                        {location.locationName || locationName}
                      </p>
                      <LinkWrapper
                        className="block p3 link hover:no-underline text-wrap break-all"
                        field={{
                          value: {
                            href: `https://www.google.com/maps/dir/Current+Location/${location.latitude},${location.longitude}`,
                          },
                        }}
                      >
                        <address className="not-italic">
                          {location.address} <br />
                          {location.city + ', ' + location.state + ' ' + location.zip}
                        </address>
                      </LinkWrapper>

                      {!location.surchargeFree && (
                        <div className="mt-2">
                          <span className="p3-medium">Surcharge: </span>
                          <span className="p3-medium text-light-other-positive">No</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Location Distance */}
                  <div className="relative">
                    <LinkWrapper
                      field={{
                        value: {
                          href: `https://www.google.com/maps/dir/Current+Location/${location.latitude},${location.longitude}`,
                        },
                      }}
                      className="location-distance flex link hover:no-underline min-w-20 justify-center"
                    >
                      <div className="flex gap-2">
                        <SVG
                          svg="icon-location-2"
                          className="w-4 h-4 [&_svg]:h-4 [&_svg]:w-4 -scale-x-100"
                        />
                        <span className="c1-medium relative">
                          {location.distanceFromCenter?.toFixed(2) || '-'}
                        </span>
                      </div>
                    </LinkWrapper>
                  </div>
                </div>
              </>
            ))}
          </div>
        </div>
      )}
      {errorMessage && locations.length <= 0 && (
        <div className="flex flex-row items-center mt-4 md:mt-0">
          <SVG
            svg="icon-error"
            className="max-w-[20px] max-h-[20px] h-5 w-5 [&_path]:fill-light-fill-feedback-negative mr-2"
          ></SVG>
          <p className="c1 text-light-fill-feedback-negative">{errorMessage}</p>
        </div>
      )}
    </>
  );
};

export default LocationList;
